import { Button, Form, Modal } from '@mairistem/composants';
import React from 'react';
import useModalSuperUserValidation from '../../../../../hooks/useModalSuperUserValidation';
import useTraitementLong from '../../../../../hooks/useTraitementLong';
import type BordereauxRejetCollectivite from '../../../../../interfaces/pieces/bordereaux/BordereauxRejetCollectivite';
import type { BordereauWithState } from '../../../../../interfaces/pieces/TreeRow';
import {
	annulerBordereau,
	rejeterBordereau,
} from '../../../../../services/piecesComptables/bordereaux/bordereaux';
import useLiquidationTreeviewStore from '../../../../../stores/liquidationTreeviewStore';

interface BordereauRejeterCollectiviteModalProps {
	onValidate: (data: BordereauxRejetCollectivite) => void;
	onClose: VoidFunction;
	bordereau: BordereauWithState;
	visible: boolean;
	isSuperUser: boolean;
	/** Annulation (bordereau d'annulation 02) plutôt que rejet (bordereau de rejet RE) : même traitement. */
	estAnnulation?: boolean;
}

/**
 * Rejet d'un bordereau à l'initiative de la collectivité, ou annulation d'un bordereau numéroté, par un job
 * (useTraitementLong) : la modale ne se ferme pas avant sa fin. L'annulation propose les mêmes options qu'une
 * annulation de pièce (ré-engagement, rattachement des charges et produits).
 */
const BordereauRejeterCollectiviteModal = ({
	onValidate,
	onClose,
	bordereau,
	visible,
	isSuperUser,
	estAnnulation = false,
}: BordereauRejeterCollectiviteModalProps) => {
	const { isValidated, validationComponent } = useModalSuperUserValidation();
	const [reengager, setReengager] = React.useState(false);
	const [isRattachChargeProduit, setIsRattachChargeProduit] =
		React.useState(false);

	const { isEnCours, lancer, traitementComponent, fermeture } =
		useTraitementLong(
			estAnnulation
				? 'Annulation du bordereau'
				: 'Rejet du bordereau par la collectivité',
			estAnnulation
				? "Erreur lors de l'annulation du bordereau."
				: 'Erreur lors du rejet du bordereau par la collectivité.',
			async (job) => {
				const { original } = JSON.parse(job.resultat) as {
					original: BordereauxRejetCollectivite;
				};
				onValidate({
					// le bordereau d'origine garde son montant ; celui du nouveau (reste à annuler de chaque pièce) est
					// relu avec l'arbre, juste après
					nouveauBordereau: original.nouveauBordereau,
					ancienBordereau: {
						...original.ancienBordereau,
						montant: bordereau.montant,
					},
				});
				const { signalerBordereau } = useLiquidationTreeviewStore.getState();
				signalerBordereau(
					original.nouveauBordereau.identifiant,
					original.nouveauBordereau.identifiantExercice,
				);
				signalerBordereau(bordereau.identifiant, bordereau.identifiantExercice);
				onClose();
			},
		);

	const handleValidate = () =>
		lancer(() =>
			(estAnnulation ? annulerBordereau : rejeterBordereau)(
				bordereau.identifiant as number,
				estAnnulation ? { reengager, isRattachChargeProduit } : {},
			),
		);

	return (
		<Modal
			{...fermeture(onClose)}
			open={visible}
			data-testid="bordereauRejeterCollectiviteModal"
		>
			<Modal.Header>
				{estAnnulation
					? 'Annuler le bordereau'
					: 'Rejeter le bordereau à l’initiative de la collectivité'}
			</Modal.Header>
			<Modal.Content>
				<p>
					{estAnnulation
						? 'Chaque pièce de ce bordereau est annulée pour le montant qui lui reste à annuler : les pièces d’annulation sont créées dans un nouveau bordereau d’annulation-réduction en brouillard, à transmettre au comptable.'
						: 'Cette action abandonne le traitement comptable de ce bordereau : il ne sera connu du comptable public et fera l’objet en interne de la création d’un bordereau de rejet pour assurer la traçabilité de l’opération.'}
				</p>
				{estAnnulation && (
					<Form>
						<Form.Checkbox
							label="Ré-engager : les montants annulés redeviennent disponibles sur les engagements ou factures repris"
							checked={reengager}
							disabled={isEnCours}
							onChange={(_e, { checked }) => setReengager(Boolean(checked))}
							data-testid="annulerBordereauReengagerCheckbox"
						/>
						<Form.Checkbox
							label="Rattachement des charges et produits : pièces d’annulation de nature 10"
							checked={isRattachChargeProduit}
							disabled={isEnCours}
							onChange={(_e, { checked }) =>
								setIsRattachChargeProduit(Boolean(checked))
							}
							data-testid="annulerBordereauRattachementCheckbox"
						/>
					</Form>
				)}
				{validationComponent}
				{traitementComponent}
			</Modal.Content>
			<Modal.Actions>
				<Button
					onClick={onClose}
					disabled={isEnCours}
					data-testid="rejeterCollectiviteModalCancel"
				>
					Annuler
				</Button>
				<Button
					disabled={isEnCours || (isSuperUser && !isValidated)}
					primary
					onClick={handleValidate}
					data-testid="rejeterCollectiviteModalConfirm"
				>
					Confirmer
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default BordereauRejeterCollectiviteModal;
